import { Modal } from 'components/Modal';
import { createContext, useCallback, useState } from 'react';
import DropArea from './DropArea';
import { Flex, notification } from 'antd';
import ChargesReview from './ChargesReview';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImportsService } from 'modules/escrow/services';
import { useParams } from 'react-router-dom';

export interface IImportCsvModalProps {
	type: 'destination' | 'transfer';
	isOpen: boolean;
	onClose: () => void;
}

export interface IUploadCsvProviderContext {
	step: UploadCsvSteps;
	file?: File | null;
	type: 'destination' | 'transfer';
	isPending: boolean;
	setStep: (step: UploadCsvSteps) => void;
	onClose: () => void;
	handleConfirmImport: () => void;
}

export enum UploadCsvSteps {
	UPLOAD = 'UPLOAD',
	REVIEW = 'REVIEW',
}

const ModalMaxSize = {
	[UploadCsvSteps.UPLOAD]: 500,
	[UploadCsvSteps.REVIEW]: 500,
};

export const UploadCsvProviderContext =
	createContext<IUploadCsvProviderContext>({
		step: UploadCsvSteps.UPLOAD,
		file: null,
		type: 'destination',
		isPending: false,
		setStep: () => {
			throw new Error('Method not implemented.');
		},
		onClose: () => {
			throw new Error('Method not implemented.');
		},
		handleConfirmImport: () => {
			throw new Error('Method not implemented.');
		},
	});

const ImportCsvModal = ({ type, isOpen, onClose }: IImportCsvModalProps) => {
	const { id } = useParams();
	const queryClient = useQueryClient();
	const [api, contextHolder] = notification.useNotification();

	const [step, setStep] = useState(UploadCsvSteps.UPLOAD);
	const [file, setFile] = useState<File | null>(null);
	const [loadingPercentage, setLoadingPercentage] = useState(0);
	const [loading, setLoading] = useState(false);

	const handleDrop = useCallback((uploadedFile: File) => {
		setFile(uploadedFile);
	}, []);

	const { mutate: createImport, isPending } = useMutation({
		mutationFn: (payload: {
			file: File;
			type: 'destination' | 'transfer';
			accountId: string;
		}) => ImportsService.createImport(payload),

		onSuccess: () => {
			api.success({
				message: 'Importação criada com sucesso',
			});

			queryClient.invalidateQueries({
				queryKey: ['imports', type],
			});

			handleClose();
		},

		onError: (e: any) => {
			api.error({
				message: 'Erro ao criar importação',
				description: e?.data?.message || 'Erro inesperado',
			});
			setLoading(false);
			setLoadingPercentage(0);
		},
	});

	const handleSetLoadingFalse = useCallback(async () => {
		return new Promise<void>(resolve => {
			setTimeout(() => {
				setLoading(false);
				resolve();
			}, 500);
		});
	}, []);

	const doUpload = useCallback(
		(uploadFile: File, uploadType: 'destination' | 'transfer') => {
			if (!id) return;

			setLoading(true);
			setLoadingPercentage(0);

			const simulateProgress = setInterval(() => {
				setLoadingPercentage(prev => {
					if (prev >= 90) {
						clearInterval(simulateProgress);
						return 90;
					}
					return prev + 10;
				});
			}, 150);

			createImport(
				{ file: uploadFile, type: uploadType, accountId: id },
				{
					onSettled: () => {
						clearInterval(simulateProgress);
						setLoadingPercentage(100);
						handleSetLoadingFalse();
					},
				},
			);
		},
		[id, createImport, handleSetLoadingFalse],
	);

	const handleNext = useCallback(() => {
		if (!file) return;

		if (type === 'transfer') {
			setStep(UploadCsvSteps.REVIEW);
			return;
		}

		doUpload(file, 'destination');
	}, [file, type, doUpload]);

	const handleConfirmImport = useCallback(() => {
		if (!file || !id) return;
		doUpload(file, 'transfer');
	}, [file, id, doUpload]);

	const handleClose = useCallback(() => {
		setFile(null);
		setStep(UploadCsvSteps.UPLOAD);
		setLoadingPercentage(0);
		setLoading(false);
		onClose();
	}, [onClose]);

	const getTitle = () => {
		if (step === UploadCsvSteps.REVIEW) return 'Confirmação';
		return `Importar ${
			type === 'destination'
				? 'cadastro de contas beneficiárias'
				: 'transferências'
		} em lote`;
	};

	return (
		<>
			{contextHolder}
			<Modal
				title={getTitle()}
				isOpen={isOpen}
				onClose={handleClose}
				width={ModalMaxSize[step]}
			>
				<Flex justify="center" align="center">
					<UploadCsvProviderContext.Provider
						value={{
							step,
							file,
							type,
							isPending,
							setStep,
							onClose: handleClose,
							handleConfirmImport,
						}}
					>
						{step === UploadCsvSteps.UPLOAD && (
							<DropArea
								onUpload={handleDrop}
								removeFile={() => setFile(null)}
								file={file}
								onNext={handleNext}
								loading={loading}
								loadPercentage={loadingPercentage}
							/>
						)}
						{step === UploadCsvSteps.REVIEW && <ChargesReview />}
					</UploadCsvProviderContext.Provider>
				</Flex>
			</Modal>
		</>
	);
};

export default ImportCsvModal;
